import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import useTheme from '@mui/material/styles/useTheme'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'

import { i18WithParams as t } from '@shared/locale'
import { formatDateSimple } from 'components-ui/src/shared/dates'
import { imageResizer } from 'components-ui/src/shared/image_resizer'
import { captureError } from 'service-api/src/shared/monitoring'
import { LocationPetProfileClientDto } from 'service-api/src/types/api'

interface TPetVaccinesRecordsFormProps {
  onFileUpload: (files: File[]) => void
  lpp?: LocationPetProfileClientDto
}

export const PetVaccinesRecordsForm = ({ onFileUpload, lpp }: TPetVaccinesRecordsFormProps) => {
  const theme = useTheme()
  const isGreaterThanMD = useMediaQuery(theme.breakpoints.up('md'))
  const [files, setFiles] = useState<File[] | null>(null)

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setFiles(acceptedFiles)
      onFileUpload(acceptedFiles)
    },
    [onFileUpload]
  )

  const onDropRejected = useCallback(() => {
    enqueueSnackbar(t('detailsPolicies.vaccinationRecords.upload.error'), {
      variant: 'error',
    })
    captureError({ cause: t('detailsPolicies.vaccinationRecords.upload.error') } as Error)
  }, [])

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      'image/jpeg': [],
      'image/jpg': [],
      'image/png': [],
      'image/gif': [],
      'application/pdf': [],
    },
    noClick: true,
    maxSize: 9000000,
  })

  const records = useMemo(() => {
    const recs = lpp?.attachments || []
    if (recs.length === 0) {
      return []
    }

    return recs
      .filter((rec) => rec.subtype === 'VET_RECORD')
      .sort((a, b) => (a.createdAt! > b.createdAt! ? -1 : 1))
      .slice(0, 5)
  }, [lpp?.attachments])

  return (
    <>
      <Typography variant="h6" sx={{ mt: 1, mb: 3 }}>
        {t('profile.pet.vaccinationRecords')}
      </Typography>
      {records.map((attachment) => (
        <Stack
          key={attachment.id}
          direction="row"
          spacing={2}
          py={1}
          mb={3}
          sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Typography variant="body1" flex={1}>
            <Link
              href={imageResizer({ bucket: attachment?.asset?.bucket, key: attachment?.asset?.key })}
              target="_blank"
              rel="noreferrer"
            >
              {attachment.asset?.displayName || 'unknown'}
            </Link>
          </Typography>
          <Typography variant="body1" sx={{ textTransform: 'lowercase' }}>{`${t(
            'global.label.added'
          )} ${formatDateSimple(new Date(attachment.createdAt!))}`}</Typography>
        </Stack>
      ))}
      <Box
        {...getRootProps()}
        sx={{
          alignItems: 'center',
          backgroundColor: theme.palette.grey[100],
          borderStyle: 'dashed',
          borderWidth: 1,
          borderColor: theme.palette.grey[400],
          display: 'flex',
          height: 148,
          justifyContent: 'center',
        }}
      >
        <input {...getInputProps()} />

        <Stack
          spacing={2}
          alignItems="center"
          sx={{
            width: !isGreaterThanMD ? '90%' : 'auto',
          }}
        >
          {isGreaterThanMD && <Typography>{t('global.label.upload')}</Typography>}
          <Button variant="outlined" onClick={open} fullWidth={!isGreaterThanMD}>
            {t('global.label.chooseFile')}
          </Button>
        </Stack>
      </Box>
      {files && files?.length > 0 ? (
        <Typography variant="caption">{files?.map((file) => `${file.name}.${file.type}`).join(', ')}</Typography>
      ) : null}
    </>
  )
}
